import {
  Box,
  FieldSelect,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { MessengerV2 } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect } from 'react';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { withQueues, WithQueuesInjectedProps } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';

interface AddModalOwnProps {
  show: boolean;
  onClose: () => void;
  onAdd: (newMessenger: MessengerV2) => void;
}

interface AddModalFormValues {
  name: string;
  queue: string;
}

type Props = WithQueuesInjectedProps &
  AddModalOwnProps &
  InjectedFormProps<
    AddModalFormValues,
    AddModalOwnProps,
    FormErrors<AddModalFormValues>
  >;

const MAX_LENGTH = 255;

const validate = (values: AddModalFormValues) => {
  const errors: FormErrors<AddModalFormValues> = {};

  if (!values.queue) {
    errors.queue = 'Please select a queue';
  }

  return errors;
};

export function AddModalImpl(props: Props): JSX.Element {
  const {
    show,
    onAdd,
    onClose,
    handleSubmit,
    queues,
    destroy: destroyForm,
  } = props;

  const messengerApi = useDixaMessengerApi();
  const notify = useNotifications();

  const handleFormSubmit = useCallback(
    async (values: AddModalFormValues) => {
      const { name, queue } = values;
      ampli.messengerFormSubmitted({ mode: 'create' });

      try {
        const newMessenger = await messengerApi.create(name, queue);

        notify({
          title: 'Messenger was added',
          text: `You can start settings up "${name}" now.`,
          icon: 'done',
          className: 'success',
          time: 5000,
        });

        onAdd(newMessenger);
      } catch {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to add the messenger.`,
          icon: 'exclamation',
          className: 'danger',
        });
      }
    },
    [notify, onAdd, messengerApi],
  );

  const handleQueueSearchToggle = useCallback(
    (open?: boolean) => {
      if (open) {
        queues.searchTermChange('');
      }
    },
    [queues],
  );

  useEffect(() => {
    if (!show) {
      destroyForm();
    }
  }, [destroyForm, show]);

  return (
    <Modal isOpen={show} onClose={onClose}>
      <Box width="600px">
        <ModalHead title="Add a new messenger" onClose={onClose} />
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <Field
              component={FieldText}
              label="Name"
              maxLength={MAX_LENGTH}
              name="name"
              placeholder="Messenger name"
              required
              type="text"
              validate={validateName}
            />
            <Field
              component={FieldSelect}
              items={queues.items}
              label="Queue"
              loading={queues.getItemsAsyncStatus === 'loading'}
              name="queue"
              placeholder="Please select a queue"
              required
              searchable
              onSearch={queues.searchTermChange}
              onToggle={handleQueueSearchToggle}
              pagination={{
                infinite: true,
                nextEnable: queues.hasNextPage,
                onNext: queues.nextPage,
              }}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={onClose}
            confirmName="Add messenger"
            confirmOnClick={handleSubmit(handleFormSubmit)}
            confirmProps={{ type: 'submit' }}
          />
        </Form>
      </Box>
    </Modal>
  );
}

export const AddModal = reduxForm<
  AddModalFormValues,
  AddModalOwnProps,
  FormErrors
>({
  enableReinitialize: true,
  form: 'createMessengerForm',
  touchOnBlur: false,
  validate,
})(withQueues(AddModalImpl, { infinite: true }));
